import * as React from 'react';
import {observer} from 'mobx-react';
import BuildingBlockHeader from './BuildingBlockHeader';
import BuildingBlockMemoryItemContainer from './BuildingBlockMemoryItemContainer';
import {DiagramInstruction} from '../../../stores/OdtExportStore';
import LineChartPreview from './LineChartPreview';
import {stylesheet} from 'typestyle';
import {NumericFormatter} from '../../../utility/FormatValueUtils';
import {Channel} from '../../../types/Channel';
import ColumnPicker from '../ColumnPicker';
import {DataKey} from '../../../stores/GeneralSelectorTableStore';
import TablePreview from './TablePreview';
import {
    BuildingBlockType,
    DiagramTableBuildingBlock,
    MergedBuildingBlockInstructions,
    NamedDateRangeWithPreviewDateRange,
    ReportBuildingBlock,
} from '../../types/Report';
import {DateIdBundle} from '../../stores/ReportEditorStore';

interface DiagramTableBuildingBlockDroppableProps {
    targetCurrency?: string;
    buildingBlock: DiagramTableBuildingBlock;
    removeBuildingBlock: (idBlock: string) => void;
    removeDiagramTraitFromBuildingBlock: (idBuildingBlock: string, diagramTrait: DiagramInstruction) => void;
    getMergedBuildingBlockInstructions: (buildingBlock: ReportBuildingBlock) => MergedBuildingBlockInstructions;
    removeTableTraitFromBuildingBlock: (idBuildingBlock: string, traitKey: string, traitToRemove: any) => void;
    changeBuildingBlockType: (idBuildingBlock: string, newBlockType: BuildingBlockType) => void;
    moneyFormatToTargetCurrency: (fractions?: number) => NumericFormatter;
    getFullChannelForChannelName: (channelName: string) => Channel;
    updateLineLimitForTableBlock: (idTableBuildingBlock: string, lineLimit: number) => void;
    updateTableColumnVisibility: (idTableBuildingBlock: string, dataKey: DataKey, visibility: boolean) => void;
    namedDateRangePackage: {
        namedDateRanges: NamedDateRangeWithPreviewDateRange[];
        selectedIdNamedDateRange: string;
        selectedIdNamedDateCompareRange?: string;
        changeSelectedNamedDateRange: (idBuildingBlock: string, dateIdBundle: DateIdBundle) => void;
    };
    selectedNamedDateRange?: NamedDateRangeWithPreviewDateRange;
    selectedNamedCompareDateRange?: NamedDateRangeWithPreviewDateRange;
    traitChecks: {
        hasToManyChannels: (reportBlocks: ReportBuildingBlock[]) => boolean;
        hasToManySearches: (reportBlocks: ReportBuildingBlock[]) => boolean;
        hasToManySortings: (reportBlocks: ReportBuildingBlock[]) => boolean;
        reportBuildingBlocksAreEmpty: (buildingBlocks: ReportBuildingBlock[]) => boolean;
        hasSearchProductMixedBuildingBlocks: (reportBlocks: ReportBuildingBlock[]) => boolean;
    };
    removeDiagramCompareTraitFromBuildingBlock: (idBuildingBlock: string) => void;
}

@observer
export default class DiagramTableBuildingBlockDroppable extends React.Component<DiagramTableBuildingBlockDroppableProps> {
    private renderDiagramPreview(mergedBuildingBlockInstructions: MergedBuildingBlockInstructions) {
        if (
            mergedBuildingBlockInstructions.diagramInstruction &&
            this.props.selectedNamedDateRange &&
            (mergedBuildingBlockInstructions.diagramInstruction.searchDiagrams.length > 0 ||
                mergedBuildingBlockInstructions.diagramInstruction.productDiagrams.length > 0)
        ) {
            if (
                mergedBuildingBlockInstructions.diagramInstruction.productDiagrams.length === 0 ||
                mergedBuildingBlockInstructions.diagramInstruction.searchDiagrams.length === 0
            ) {
                return (
                    <div className={diagramTableBlockCss.diagramPreviewContainer}>
                        <LineChartPreview
                            targetCurrency={this.props.targetCurrency}
                            dateRange={this.props.selectedNamedDateRange.previewDateRange}
                            compareDateRange={
                                this.props.selectedNamedCompareDateRange ? this.props.selectedNamedCompareDateRange.previewDateRange : undefined
                            }
                            mergedBuildingBlockInstructions={mergedBuildingBlockInstructions}
                            getFullChannelForChannelName={this.props.getFullChannelForChannelName}
                        />
                    </div>
                );
            }
        }
        return null;
    }

    private renderTablePreview(mergedTableData: MergedBuildingBlockInstructions) {
        const {productDiagrams, searchDiagrams} = mergedTableData.diagramInstruction
            ? mergedTableData.diagramInstruction
            : {productDiagrams: [], searchDiagrams: []};
        const {activeViews} = mergedTableData.tableInstruction ? mergedTableData.tableInstruction : {activeViews: []};

        if ((productDiagrams.length > 0 || searchDiagrams.length > 0) && activeViews.length === 1 && this.props.selectedNamedDateRange) {
            return (
                <div className={diagramTableBlockCss.tablePreviewContainer}>
                    <div className={diagramTableBlockCss.limitColumnPickerContainer}>
                        <ColumnPicker
                            idParentBuildingBlock={this.props.buildingBlock.id}
                            updateTableColumnVisibility={this.props.updateTableColumnVisibility}
                            columns={this.props.buildingBlock.columns}
                        />
                    </div>
                    <TablePreview
                        compareDateRange={
                            this.props.selectedNamedCompareDateRange ? this.props.selectedNamedCompareDateRange.previewDateRange : undefined
                        }
                        targetCurrency={this.props.targetCurrency}
                        previewModeEnabled={false}
                        diagramCompanionMode={true}
                        lineLimit={0}
                        columns={this.props.buildingBlock.columns}
                        dateRange={this.props.selectedNamedDateRange.previewDateRange}
                        mergedBuildingBlockInstructions={mergedTableData}
                        moneyFormatToTargetCurrency={this.props.moneyFormatToTargetCurrency}
                        getFullChannelForChannelName={this.props.getFullChannelForChannelName}
                    />
                </div>
            );
        }
        return null;
    }

    public render() {
        const {removeBuildingBlock, buildingBlock, traitChecks, removeDiagramCompareTraitFromBuildingBlock} = this.props;
        const mergedDiagramInstructions = this.props.getMergedBuildingBlockInstructions(buildingBlock);

        return (
            <div>
                <BuildingBlockHeader
                    namedDateRangePackage={this.props.namedDateRangePackage}
                    idBuildingBlock={this.props.buildingBlock.id}
                    changeBuildingBlockType={this.props.changeBuildingBlockType}
                    removeBuildingBlock={() => removeBuildingBlock(buildingBlock.id)}
                    buildingBlockType={this.props.buildingBlock.blockType}
                />
                {this.renderDiagramPreview(mergedDiagramInstructions)}
                {this.renderTablePreview(mergedDiagramInstructions)}
                <BuildingBlockMemoryItemContainer
                    traitChecks={traitChecks}
                    idParentBlock={buildingBlock.id}
                    removeDiagramTraitFromBuildingBlock={this.props.removeDiagramTraitFromBuildingBlock}
                    removeTableTraitFromBuildingBlock={this.props.removeTableTraitFromBuildingBlock}
                    buildingBlock={this.props.buildingBlock}
                    mergedBuildingBlockInstructions={this.props.getMergedBuildingBlockInstructions(this.props.buildingBlock)}
                    removeDiagramCompareTraitFromBuildingBlock={removeDiagramCompareTraitFromBuildingBlock}
                />
            </div>
        );
    }
}

const diagramTableBlockCss = stylesheet({
    diagramPreviewContainer: {
        position: 'relative',
        height: 300,
    },
    tablePreviewContainer: {
        position: 'relative',
    },
    limitColumnPickerContainer: {
        display: 'flex',
        height: '100%',
        justifyContent: 'flex-end',
    },
});



// WEBPACK FOOTER //
// ./src/report/views/PaperSheetBlocks/DiagramTableBuildingBlockDroppable.tsx